import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaChartBar, FaCloud, FaCode, FaRobot, FaHeadset, FaBullhorn, FaServer, FaDatabase, FaMobileAlt } from 'react-icons/fa';
import Typewriter from '../components/Typewriter';
import axios from 'axios';

const API_URL = 'https://aamirsaba.com/api';

const Home = () => {
  const [homeData, setHomeData] = useState({
    heroTitle: 'Future-Proof Your Business',
    heroHighlight: 'Business',
    heroHighlight2: 'AI Solutions',
    heroSubtitle: 'We deliver cutting-edge IT solutions, professional training, and automation services to help businesses scale globally.',
    heroCtaText: 'Explore Services',
    heroCtaText2: 'Free Consultation',
    servicesTitle: 'What We Offer',
    servicesSubtitle: 'Comprehensive solutions to accelerate your digital journey',
    trustTitle: 'Trusted by businesses worldwide',
    stats: { clients: '50+', services: '12+', experience: '4+' }
  });

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const iconMap = {
    'Power BI Training': FaChartBar,
    'ITIL 4 Certification': FaHeadset,
    'WhatsApp Automation': FaRobot,
    'SEO Automation': FaCloud,
    'Custom Development': FaCode,
    'Custom Software Development': FaCode,
    'Website Development': FaServer,
    'Textile POS Solution': FaDatabase,
    'IT Consulting': FaMobileAlt,
    'Digital Marketing': FaBullhorn,
    'Hosting & Email': FaServer,
    'Hosting & Email Solutions': FaServer,
  };

  const getIcon = (title) => {
    const Icon = iconMap[title];
    return Icon || FaServer;
  };

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/home`),
      axios.get(`${API_URL}/services`)
    ])
      .then(([homeResponse, servicesResponse]) => {
        if (homeResponse.data && Object.keys(homeResponse.data).length > 0) {
          setHomeData(prev => ({ ...prev, ...homeResponse.data }));
        }
        setServices(servicesResponse.data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading data:', error);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <div className="text-cyan text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div>
      <section className="relative min-h-screen flex items-center bg-gradient-to-br from-navy via-darkNavy to-navy overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-cyan rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-orange rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="inline-block px-4 py-2 bg-cyan/10 text-cyan rounded-full text-sm font-semibold mb-6">
              🇬🇧 UK & 🇺🇸 USA Registered
            </span>
            <h1 className="text-4xl md:text-7xl font-bold leading-tight">
              {homeData.heroTitle}
              <br />
              <span className="text-cyan">{homeData.heroHighlight}</span> with
              <br />
              <Typewriter words={[homeData.heroHighlight2 || 'AI Solutions', 'Expert Training', 'Digital Transformation']} />
            </h1>
            <p className="text-gray-300 text-lg md:text-xl mt-6 max-w-2xl">
              {homeData.heroSubtitle}
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link to="/services" className="px-8 py-3 bg-cyan text-navy rounded-full font-semibold hover:bg-cyan/80 transition shadow-lg shadow-cyan/25">
                {homeData.heroCtaText || 'Explore Services'}
              </Link>
              <Link to="/contact" className="px-8 py-3 border border-cyan text-cyan rounded-full font-semibold hover:bg-cyan/10 transition">
                {homeData.heroCtaText2 || 'Free Consultation'}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-darkNavy">
        <div className="container mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold">
              {homeData.servicesTitle || 'What We Offer'}
            </h2>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
              {homeData.servicesSubtitle || 'Comprehensive solutions to accelerate your digital journey'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.slice(0, 6).map((service, index) => {
              const IconComponent = getIcon(service.title);
              return (
                <motion.div
                  key={service.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  whileHover={{ y: -5 }}
                  className="bg-navy rounded-xl p-6 border border-gray-800 hover:border-cyan/50 transition group"
                >
                  <div className="text-4xl text-cyan mb-4 group-hover:scale-110 transition">
                    <IconComponent />
                  </div>
                  <span className="text-xs text-cyan/60 uppercase tracking-wider">{service.category}</span>
                  <h3 className="text-xl font-semibold mb-2 mt-1 text-white">{service.title}</h3>
                  <p className="text-gray-400 text-sm">{service.description}</p>
                  <Link 
                    to={`/services/${service.slug}`} 
                    className="inline-block mt-4 text-cyan hover:text-cyan/80 text-sm font-medium hover:underline transition"
                  >
                    Learn More →
                  </Link>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services" className="px-8 py-3 border border-cyan text-cyan rounded-full font-semibold hover:bg-cyan/10 transition">
              View All Services →
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-navy border-y border-gray-800">
        <div className="container mx-auto px-6 text-center">
          <p className="text-gray-400 text-sm uppercase tracking-wider mb-8">
            {homeData.trustTitle || 'Trusted by businesses worldwide'}
          </p>
          <div className="flex flex-wrap justify-center items-center gap-12">
            <div className="flex items-center gap-2">
              <span className="text-3xl">🇬🇧</span>
              <span className="text-white font-semibold">UK Registered</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-3xl">🇺🇸</span>
              <span className="text-white font-semibold">USA Registered</span>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-cyan">{homeData.stats?.clients || '50+'}</div>
              <div className="text-gray-400 text-sm">Happy Clients</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-cyan">{homeData.stats?.services || '12+'}</div>
              <div className="text-gray-400 text-sm">Services</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-cyan">{homeData.stats?.experience || '4+'}</div>
              <div className="text-gray-400 text-sm">Years Experience</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
